import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiStar, FiZap } from "react-icons/fi";
import ProductCard from "../components/product/ProductCard";
import { getFeaturedProducts, getNewArrivals } from "../data/products";

/* ── SVG Category Icons ── */
const DroneIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
    <circle cx="24" cy="24" r="7" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="24" cy="24" r="3" fill="currentColor" opacity="0.3"/>
    <line x1="17" y1="24" x2="8" y2="24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="31" y1="24" x2="40" y2="24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="7" cy="24" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="41" cy="24" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <line x1="24" y1="17" x2="24" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="24" y1="31" x2="24" y2="40" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="24" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="24" cy="41" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);
const AudioIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
    <path d="M8 30V18a16 16 0 0132 0v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="4" y="27" width="8" height="12" rx="4" stroke="currentColor" strokeWidth="2"/>
    <rect x="36" y="27" width="8" height="12" rx="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M40 38v2a6 6 0 01-6 6h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const WatchIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
    <rect x="13" y="12" width="22" height="24" rx="5" stroke="currentColor" strokeWidth="2"/>
    <path d="M18 12V8a2 2 0 012-2h8a2 2 0 012 2v4M18 36v4a2 2 0 002 2h8a2 2 0 002-2v-4" stroke="currentColor" strokeWidth="2"/>
    <path d="M19 24h5l3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="24" cy="24" r="1.5" fill="currentColor"/>
    <path d="M35 20h2M35 28h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
    <rect x="4" y="14" width="40" height="26" rx="5" stroke="currentColor" strokeWidth="2"/>
    <circle cx="24" cy="27" r="7" stroke="currentColor" strokeWidth="2"/>
    <circle cx="24" cy="27" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M15 14l3-6h10l3 6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <circle cx="36" cy="21" r="2" fill="currentColor" opacity="0.4"/>
  </svg>
);
const GadgetIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
    <path d="M24 6l4 10h10l-8 6 3 10-9-6-9 6 3-10-8-6h10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M24 34v8M18 42h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const categoryData = [
  { id: "Drones",       label: "Drones",       Icon: DroneIcon,  color: "from-violet-500/20 to-brand-600/10",  text: "text-brand-600"  },
  { id: "Audio",        label: "Audio",         Icon: AudioIcon,  color: "from-rose-500/15 to-pink-400/10",     text: "text-rose-500"   },
  { id: "Wearables",    label: "Wearables",     Icon: WatchIcon,  color: "from-emerald-500/15 to-teal-400/10", text: "text-emerald-600" },
  { id: "Cameras",      label: "Cameras",       Icon: CameraIcon, color: "from-amber-500/15 to-orange-400/10", text: "text-amber-600"  },
  { id: "Smart Gadgets",label: "Smart Gadgets", Icon: GadgetIcon, color: "from-cyan-500/15 to-sky-400/10",     text: "text-cyan-600"   },
];

const benefits = [
  {
    title: "1-Year Warranty",
    desc: "Every product backed by a full 12-month warranty.",
    Icon: () => (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V6l-9-4z" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    grad: "from-brand-500 to-brand-700",
  },
  {
    title: "Free Shipping",
    desc: "Free fast delivery on orders over $99. Only $20 flat rate below that.",
    Icon: () => (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="1.8">
        <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="5.5" cy="18.5" r="2.5" strokeLinecap="round"/>
        <circle cx="18.5" cy="18.5" r="2.5" strokeLinecap="round"/>
      </svg>
    ),
    grad: "from-emerald-500 to-teal-600",
  },
  {
    title: "30-Day Returns",
    desc: "Not happy? Return within 30 days, no questions asked.",
    Icon: () => (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 12a9 9 0 109-9H8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 7l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    grad: "from-amber-500 to-orange-600",
  },
  {
    title: "24/7 Support",
    desc: "Real tech experts ready to help around the clock.",
    Icon: () => (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="1.8">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    grad: "from-rose-500 to-pink-600",
  },
];

const testimonials = [
  { name: "Marcus T.", rating: 5, text: "The AeroPro X4 is insane for the price. Crystal clear 4K footage and it fit in my hiking pack. Shipping was lightning fast.", product: "AeroPro X4 Mini Drone", avatar: "MT" },
  { name: "Priya S.", rating: 5, text: "SoundWave Pro ANC headphones are my favourite purchase of the year. The noise cancellation is incredible and they're so comfortable.", product: "SoundWave Pro ANC Headphones", avatar: "PS" },
  { name: "Jordan K.", rating: 4, text: "Nexus Watch Ultra tracks everything I need and the AMOLED screen is gorgeous. Battery lasts all week. Highly recommend.", product: "Nexus Watch Ultra", avatar: "JK" },
];

/* ── Wave SVG dividers ── */
const WaveDown = ({ from = "#fff", to = "#fafaff" }) => (
  <div style={{ background: to, marginTop: -1 }}>
    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: 60, display: "block" }}>
      <path d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,0 L0,0 Z" fill={from}/>
    </svg>
  </div>
);
const WaveUp = ({ from = "#fff", to = "#fafaff" }) => (
  <div style={{ background: to, marginBottom: -1 }}>
    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: 60, display: "block" }}>
      <path d="M0,40 C240,0 480,80 720,40 C960,0 1200,80 1440,40 L1440,80 L0,80 Z" fill={from}/>
    </svg>
  </div>
);

const fadeUp = {
  hidden:  { opacity: 0, y: 32 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] } }),
};

const HomePage = () => {
  const featured   = getFeaturedProducts();
  const newArrivals = getNewArrivals();

  return (
    <div className="overflow-x-hidden">

      {/* ──────────────── HERO ──────────────── */}
      <section className="relative bg-mesh-hero min-h-[92vh] flex items-center overflow-hidden">

        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full bg-brand-600/20 blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-56 h-56 rounded-full bg-accent-500/15 blur-3xl animate-float pointer-events-none" style={{ animationDelay: "3s" }} />

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px"
        }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 grid lg:grid-cols-2 gap-12 items-center">

          {/* Left — copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white/8 border border-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8"
            >
              <span className="w-2 h-2 bg-accent-400 rounded-full animate-glow-pulse" />
              <span className="text-white/80 text-sm font-sans">New arrivals just dropped</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6"
            >
              Next-Gen<br />
              <span className="gradient-text">Compact</span><br />
              Electronics.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-white/60 text-lg font-sans leading-relaxed mb-10 max-w-lg"
            >
              Premium drones, headphones, smartwatches, action cameras & smart gadgets — all compact, all powerful.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/shop" className="btn-primary px-8 py-4 text-base shadow-glow">
                Shop Now <FiArrowRight size={18} />
              </Link>
              <Link to="/shop?badge=New"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-display font-semibold text-white border border-white/20 rounded-2xl hover:bg-white/10 backdrop-blur-sm transition-all duration-200">
                New Arrivals
              </Link>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-8 mt-14 pt-10 border-t border-white/10"
            >
              {[["25+","Products"],["4.6★","Avg Rating"],["5K+","Customers"],["$99+","Free Ship"]].map(([val, label]) => (
                <div key={label}>
                  <p className="font-display text-2xl font-bold text-white">{val}</p>
                  <p className="text-sm text-white/40 font-sans">{label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — decorative SVG visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:flex items-center justify-center"
          >
            <div className="relative w-[420px] h-[420px]">
              {/* Outer glow ring */}
              <div className="absolute inset-0 rounded-full border border-brand-500/20 animate-spin-slow" />
              <div className="absolute inset-8 rounded-full border border-accent-400/20 animate-spin-slow" style={{ animationDirection: "reverse", animationDuration: "12s" }} />

              {/* Centre hexagon graphic */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg viewBox="0 0 200 200" fill="none" className="w-64 h-64 drop-shadow-2xl animate-float">
                  <defs>
                    <linearGradient id="hexGrad" x1="0" y1="0" x2="200" y2="200">
                      <stop offset="0%" stopColor="#7026f7" stopOpacity="0.9"/>
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.7"/>
                    </linearGradient>
                    <linearGradient id="hexGrad2" x1="0" y1="0" x2="200" y2="200">
                      <stop offset="0%" stopColor="#9f75ff" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.2"/>
                    </linearGradient>
                  </defs>
                  {/* Hexagon shell */}
                  <polygon points="100,10 185,57.5 185,142.5 100,190 15,142.5 15,57.5" fill="url(#hexGrad2)" stroke="url(#hexGrad)" strokeWidth="1.5"/>
                  {/* Inner smaller hex */}
                  <polygon points="100,40 160,72.5 160,127.5 100,160 40,127.5 40,72.5" fill="url(#hexGrad)" opacity="0.25"/>
                  {/* Lightning bolt */}
                  <path d="M108 60l-22 48h18l-10 36 28-54h-20l6-30z" fill="white" opacity="0.9"/>
                  {/* Orbiting dots */}
                  <circle cx="185" cy="100" r="5" fill="#f59e0b" opacity="0.8"/>
                  <circle cx="15"  cy="100" r="4" fill="#7026f7" opacity="0.7"/>
                  <circle cx="100" cy="10"  r="4" fill="#9f75ff" opacity="0.7"/>
                  <circle cx="100" cy="190" r="5" fill="#fbbf24" opacity="0.6"/>
                </svg>
              </div>

              {/* Floating mini cards */}
              {[
                { label: "4K Drone",    val: "$89",  top: "8%",  left: "-10%", delay: 0 },
                { label: "ANC Audio",   val: "$79",  top: "70%", left: "-8%",  delay: 0.5 },
                { label: "Smart Watch", val: "$149", top: "10%", right: "-8%", delay: 1 },
              ].map(({ label, val, top, left, right, delay }) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + delay, duration: 0.4 }}
                  style={{ position: "absolute", top, left, right }}
                  className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-4 py-2.5 text-white shadow-lg"
                >
                  <p className="text-xs text-white/60 font-sans">{label}</p>
                  <p className="font-display font-bold text-base">{val}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none" style={{ height: 60, display: "block", width: "100%" }}>
            <path d="M0,40 C360,80 1080,0 1440,50 L1440,80 L0,80 Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* ──────────────── CATEGORIES ──────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="section-tag">Browse</span>
            <h2 className="section-heading">Shop by Category</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {categoryData.map((cat, i) => (
              <motion.div key={cat.id} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link to={`/shop?category=${cat.id}`}
                  className={`flex flex-col items-center gap-4 p-6 rounded-3xl bg-gradient-to-br ${cat.color} border border-gray-100 hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 group`}>
                  <div className={`${cat.text} transition-transform duration-300 group-hover:scale-110`}>
                    <cat.Icon />
                  </div>
                  <span className="font-display font-semibold text-gray-800 text-sm text-center group-hover:text-gray-900">
                    {cat.label}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WaveDown from="white" to="#fafaff" />

      {/* ──────────────── BEST SELLERS ──────────────── */}
      <section className="py-24 bg-surface-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="section-tag">Top Picks</span>
              <h2 className="section-heading">Best Sellers</h2>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center gap-1.5 font-display font-semibold text-brand-600 hover:gap-3 transition-all duration-200 text-sm">
              View All <FiArrowRight size={15} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      <WaveUp from="#fafaff" to="white" />

      {/* ──────────────── PROMO BANNER ──────────────── */}
      <section className="py-10 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] bg-mesh-dark p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8"
          >
            {/* Grid overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-30" style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
              backgroundSize: "40px 40px"
            }} />
            {/* Glow orbs */}
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-brand-600/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-accent-500/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <FiZap size={16} className="text-accent-400" />
                <span className="text-accent-400 font-display font-semibold text-sm uppercase tracking-widest">Limited Time</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                Save up to 35% off<br />compact electronics
              </h3>
              <p className="text-white/50 font-sans text-sm">Shop our sale collection — prices as low as $14.99.</p>
            </div>

            <Link to="/shop?badge=Sale"
              className="relative z-10 flex-shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-700 font-display font-bold rounded-2xl hover:bg-brand-50 hover:shadow-glow-gold transition-all duration-200 text-base">
              Shop the Sale <FiArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ──────────────── NEW ARRIVALS ──────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="section-tag">Fresh In</span>
              <h2 className="section-heading">New Arrivals</h2>
            </div>
            <Link to="/shop?badge=New" className="hidden sm:flex items-center gap-1.5 font-display font-semibold text-brand-600 hover:gap-3 transition-all duration-200 text-sm">
              See All <FiArrowRight size={15} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newArrivals.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      <WaveDown from="white" to="#fafaff" />

      {/* ──────────────── BENEFITS ──────────────── */}
      <section className="py-24 bg-surface-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="section-tag">Why Us</span>
            <h2 className="section-heading">The StoreOnline Difference</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="group bg-white rounded-3xl p-7 border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-13 h-13 w-12 h-12 rounded-2xl bg-gradient-to-br ${b.grad} flex items-center justify-center mb-5 text-white shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <b.Icon />
                </div>
                <h4 className="font-display font-bold text-gray-900 mb-2 text-base">{b.title}</h4>
                <p className="text-sm text-gray-500 font-sans leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WaveUp from="#fafaff" to="white" />

      {/* ──────────────── TESTIMONIALS ──────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="section-tag">Reviews</span>
            <h2 className="section-heading">What Our Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative bg-white rounded-3xl p-7 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden"
              >
                {/* Quote mark SVG */}
                <svg className="absolute top-5 right-6 w-16 h-16 text-brand-100" viewBox="0 0 64 64" fill="currentColor">
                  <path d="M0 32C0 14.327 14.327 0 32 0s32 14.327 32 32-14.327 32-32 32S0 49.673 0 32zm20-8h-6v8h6V24zm10 0h-6v8h6V24zm-8 12v4c0 3.313 2.687 6 6 6h2v-4h-2c-1.105 0-2-.895-2-2v-4h-4zm10 0v4c0 3.313 2.687 6 6 6h2v-4h-2c-1.105 0-2-.895-2-2v-4h-4z" opacity="0.4"/>
                </svg>

                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <FiStar key={j} size={14} className="text-accent-400 fill-accent-400" />
                  ))}
                </div>
                <p className="text-gray-700 font-sans leading-relaxed mb-5 text-[15px]">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-xs font-display font-bold">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-display font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-400 font-sans">Verified · {t.product}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── NEWSLETTER ──────────────── */}
      <section className="relative py-24 bg-mesh-dark overflow-hidden">
        {/* Decorative rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[600px] rounded-full border border-white/5" />
          <div className="absolute w-[400px] h-[400px] rounded-full border border-white/5" />
          <div className="absolute w-[200px] h-[200px] rounded-full border border-white/5" />
        </div>

        <div className="relative max-w-xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-block text-accent-400 text-xs font-display font-semibold uppercase tracking-[0.2em] mb-4">Newsletter</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Get Exclusive Deals First
            </h2>
            <p className="text-white/40 font-sans mb-8">
              Subscribe and be first to know about new drops, flash sales, and tech guides.
            </p>
            <form className="flex gap-2 max-w-sm mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3.5 bg-white/8 border border-white/15 rounded-2xl text-white placeholder-white/30 focus:border-brand-400 focus:bg-white/12 outline-none text-sm font-sans backdrop-blur-sm transition-all"
              />
              <button type="submit" className="btn-primary px-5 shadow-glow">
                <FiArrowRight size={17} />
              </button>
            </form>
            <p className="text-xs text-white/25 font-sans mt-3">No spam. Unsubscribe anytime.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
